/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Semantic Color Utilities
 *
 * Generates semantic colors (success, warning, error, info) from a primary color.
 * Provides algorithms to derive meaningful semantic colors that maintain visual
 * consistency while providing clear distinctions for different UI states.
 */

import { hexToHsl, parseHsl } from "./color-utils";

export interface SemanticColors {
  success: string; // HSL format
  warning: string;
  error: string;
  info: string;
  successForeground: string;
  warningForeground: string;
  errorForeground: string;
  infoForeground: string;
}

export interface SemanticColorOptions {
  /** Primary color in hex format */
  primaryColor: string;
  /** Whether to preserve standard semantic color meanings (green success, red error) */
  preserveStandardMeanings?: boolean;
  /** Custom adjustments for each semantic color */
  adjustments?: {
    success?: {
      hueShift?: number;
      saturationAdjust?: number;
      lightnessAdjust?: number;
    };
    warning?: {
      hueShift?: number;
      saturationAdjust?: number;
      lightnessAdjust?: number;
    };
    error?: {
      hueShift?: number;
      saturationAdjust?: number;
      lightnessAdjust?: number;
    };
    info?: {
      hueShift?: number;
      saturationAdjust?: number;
      lightnessAdjust?: number;
    };
  };
}

/**
 * Derives a semantic color from the primary color by shifting hue and adjusting saturation/lightness
 */
function deriveSemanticColor(
  primaryColor: string,
  hueShift: number,
  saturationAdjust: number = 0,
  lightnessAdjust: number = 0,
): string {
  const primaryHsl = hexToHsl(primaryColor);
  const primaryParsed = parseHsl(primaryHsl);

  // Shift hue (wrap around 360)
  const newHue = (primaryParsed.h + hueShift + 360) % 360;

  // Adjust saturation (clamp between 0-100)
  const newSaturation = Math.max(
    20, // Minimum saturation for visibility
    Math.min(100, primaryParsed.s + saturationAdjust),
  );

  // Adjust lightness (clamp between 10-90 for visibility)
  const newLightness = Math.max(
    20, // Minimum lightness for visibility
    Math.min(80, primaryParsed.l + lightnessAdjust),
  );

  // Round all values to whole numbers for CSS compatibility
  return `${Math.round(newHue)} ${Math.round(newSaturation)}% ${Math.round(newLightness)}%`;
}

/**
 * Generates a contrasting foreground color for semantic backgrounds
 */
function getContrastingForeground(hsl: string): string {
  const parsed = parseHsl(hsl);

  // For dark backgrounds, use light text
  // For light backgrounds, use dark text
  if (parsed.l < 50) {
    return "0 0% 98%"; // Light text
  } else {
    return "0 0% 15%"; // Dark text
  }
}

/**
 * Generates semantic colors from a primary color using hue shifts
 *
 * Algorithm:
 * - Success: Shift hue +120° (toward green) with increased saturation
 * - Warning: Shift hue +60° (toward yellow) with increased saturation and lightness
 * - Error: Shift hue -60° (toward red) with increased saturation
 * - Info: Shift hue -120° (toward blue) with slight saturation increase
 */
export function generateSemanticColors(
  options: SemanticColorOptions,
): SemanticColors {
  const {
    primaryColor,
    preserveStandardMeanings = true,
    adjustments = {},
  } = options;

  if (preserveStandardMeanings) {
    // Use standard semantic colors (green success, red error, etc.)
    // but blend their hues with the primary color to harmonize with the theme
    // This creates theme-responsive semantic colors while maintaining meaning
    const primaryParsed = parseHsl(hexToHsl(primaryColor));

    // Blend function: mixes standard semantic hue with primary hue
    // blendFactor of 0.3 = 30% primary hue, 70% standard semantic hue (maintains meaning)
    const blendHue = (
      standardHue: number,
      primaryHue: number,
      blendFactor: number = 0.3,
    ): number => {
      // Handle hue wrapping (0-360)
      const diff = ((standardHue - primaryHue + 180) % 360) - 180;
      const blended = primaryHue + diff * (1 - blendFactor);
      return Math.round((blended + 360) % 360);
    };

    // Success: Blend standard green (142) with primary hue, adjust saturation/lightness
    const successLightness = Math.min(55, Math.max(40, primaryParsed.l));
    const successHue = blendHue(142, primaryParsed.h, 0.25); // 25% primary, 75% green (maintain green meaning)
    const successSaturation = Math.round(
      Math.max(50, Math.min(80, 65 + (primaryParsed.s - 50) * 0.3)),
    );
    const successHsl = adjustments.success
      ? deriveSemanticColor(
          primaryColor,
          adjustments.success.hueShift ?? successHue - primaryParsed.h,
          adjustments.success.saturationAdjust ??
            successSaturation - primaryParsed.s,
          adjustments.success.lightnessAdjust ??
            successLightness - primaryParsed.l,
        )
      : `${successHue} ${successSaturation}% ${Math.round(successLightness)}%`;

    // Warning: Blend standard orange/yellow (38) with primary hue
    const warningLightness = Math.min(60, Math.max(45, primaryParsed.l + 5));
    const warningHue = blendHue(38, primaryParsed.h, 0.3); // 30% primary, 70% orange
    const warningSaturation = Math.round(
      Math.max(60, Math.min(95, 85 + (primaryParsed.s - 50) * 0.25)),
    );
    const warningHsl = adjustments.warning
      ? deriveSemanticColor(
          primaryColor,
          adjustments.warning.hueShift ?? warningHue - primaryParsed.h,
          adjustments.warning.saturationAdjust ??
            warningSaturation - primaryParsed.s,
          adjustments.warning.lightnessAdjust ??
            warningLightness - primaryParsed.l,
        )
      : `${warningHue} ${warningSaturation}% ${Math.round(warningLightness)}%`;

    // Error: Blend standard red (0) with primary hue (less blending to maintain red meaning)
    const errorLightness = Math.min(50, Math.max(40, primaryParsed.l - 5));
    const errorHue = blendHue(0, primaryParsed.h, 0.2); // 20% primary, 80% red (strong red meaning)
    const errorSaturation = Math.round(
      Math.max(60, Math.min(90, 75 + (primaryParsed.s - 50) * 0.3)),
    );
    const errorHsl = adjustments.error
      ? deriveSemanticColor(
          primaryColor,
          adjustments.error.hueShift ?? errorHue - primaryParsed.h,
          adjustments.error.saturationAdjust ??
            errorSaturation - primaryParsed.s,
          adjustments.error.lightnessAdjust ?? errorLightness - primaryParsed.l,
        )
      : `${errorHue} ${errorSaturation}% ${Math.round(errorLightness)}%`;

    // Info: Blend standard blue (221) with primary hue
    const infoLightness = Math.min(55, Math.max(45, primaryParsed.l));
    const infoHue = blendHue(221, primaryParsed.h, 0.3); // 30% primary, 70% blue
    const infoSaturation = Math.round(
      Math.max(55, Math.min(85, 80 + (primaryParsed.s - 50) * 0.2)),
    );
    const infoHsl = adjustments.info
      ? deriveSemanticColor(
          primaryColor,
          adjustments.info.hueShift ?? infoHue - primaryParsed.h,
          adjustments.info.saturationAdjust ?? infoSaturation - primaryParsed.s,
          adjustments.info.lightnessAdjust ?? infoLightness - primaryParsed.l,
        )
      : `${infoHue} ${infoSaturation}% ${Math.round(infoLightness)}%`;

    return {
      success: successHsl,
      warning: warningHsl,
      error: errorHsl,
      info: infoHsl,
      successForeground: getContrastingForeground(successHsl),
      warningForeground: getContrastingForeground(warningHsl),
      errorForeground: getContrastingForeground(errorHsl),
      infoForeground: getContrastingForeground(infoHsl),
    };
  } else {
    // Derive semantic colors directly from primary using hue shifts
    const successHsl = deriveSemanticColor(
      primaryColor,
      adjustments.success?.hueShift ?? 120,
      adjustments.success?.saturationAdjust ?? 15,
      adjustments.success?.lightnessAdjust ?? 5,
    );

    const warningHsl = deriveSemanticColor(
      primaryColor,
      adjustments.warning?.hueShift ?? 60,
      adjustments.warning?.saturationAdjust ?? 20,
      adjustments.warning?.lightnessAdjust ?? 10,
    );

    const errorHsl = deriveSemanticColor(
      primaryColor,
      adjustments.error?.hueShift ?? -60,
      adjustments.error?.saturationAdjust ?? 25,
      adjustments.error?.lightnessAdjust ?? -5,
    );

    const infoHsl = deriveSemanticColor(
      primaryColor,
      adjustments.info?.hueShift ?? -120,
      adjustments.info?.saturationAdjust ?? 10,
      adjustments.info?.lightnessAdjust ?? 5,
    );

    return {
      success: successHsl,
      warning: warningHsl,
      error: errorHsl,
      info: infoHsl,
      successForeground: getContrastingForeground(successHsl),
      warningForeground: getContrastingForeground(warningHsl),
      errorForeground: getContrastingForeground(errorHsl),
      infoForeground: getContrastingForeground(infoHsl),
    };
  }
}

/**
 * Generates semantic colors with standard meanings (green success, red error, etc.)
 * but harmonized with the primary color's tone
 */
export function generateHarmonizedSemanticColors(
  primaryColor: string,
): SemanticColors {
  return generateSemanticColors({
    primaryColor,
    preserveStandardMeanings: true,
  });
}

/**
 * Generates semantic colors derived directly from the primary color
 */
export function generateDerivedSemanticColors(
  primaryColor: string,
): SemanticColors {
  return generateSemanticColors({
    primaryColor,
    preserveStandardMeanings: false,
  });
}
